import BackButton from '../misc/BackButton'
import {
  QUARTER_LAYOUT,
  QUARTER_MATCH_FIELDS,
  ROUND_ONE_LAYOUT,
  ROUND_TWO_LAYOUT,
  ROUND_TWO_MATCH_FIELDS,
  SEMI_LAYOUT,
  SEMI_MATCH_FIELDS,
} from './bracketStructureV2'
import KnockoutMatchupV2 from './KnockoutMatchupV2'
import { buildKnockoutStageV2 } from './knockoutStageV2'
import { Button } from 'antd'
import React, { useEffect, useRef, useState } from 'react'

const buildTeamOption = (teamId, rankings, sourceLabel) => {
  const ranking = rankings[String(teamId)]

  return ranking
    ? { ...ranking, value: teamId, sourceLabel }
    : { value: teamId, name: String(teamId), rank: null, flag: '/flags/Mystery.png', sourceLabel }
}

const formatRoundDate = (date) => String(date || '').replace(/,\s*20\d{2}$/, '')

const renderConfiguredRound = ({ bracket, configs, handleChange, rankings }) => {
  return configs.map(({ field, label, options }) => (
    <KnockoutMatchupV2
      key={field}
      bracket={bracket}
      field={field}
      label={label}
      onChange={handleChange}
      options={options.map(option => option.value ? option : (bracket[option.field] ? buildTeamOption(bracket[option.field], rankings, option.sourceLabel) : null))}
    />
  ))
}

export default function BracketFormBracketV2({
  advancingThirdPlaceTeams,
  autoFillKnockout,
  clearKnockout,
  groupRankings,
  handleChange,
  isAdmin,
  knockoutPicks,
  rankings,
  roundOneContext,
  saveLabel,
  saveBracket,
  savePending,
  scorecard,
  setStep,
  showBackButton,
}) {
  const mobileScrollerRef = useRef(null)
  const [mobileFocusIndex, setMobileFocusIndex] = useState(0)
  const { roundOnePicks, roundOneSourceLabels } = roundOneContext
  const { roundOf32 } = buildKnockoutStageV2(groupRankings, advancingThirdPlaceTeams)
  const bracket = { ...roundOnePicks, ...knockoutPicks }
  const winnerFlag = rankings[String(bracket.pick_winner)]?.flag ?? '/flags/Mystery.png'

  const roundOneByMatchNumber = roundOf32.reduce((matchMap, matchup) => {
    matchMap[matchup.matchNumber] = matchup
    return matchMap
  }, {})

  const thirdPlaceOptions = () => {
    const leftLoser = bracket.pick_f1
      ? [bracket.pick_s1, bracket.pick_s2].find(teamId => teamId && teamId !== bracket.pick_f1)
      : null
    const rightLoser = bracket.pick_f2
      ? [bracket.pick_s3, bracket.pick_s4].find(teamId => teamId && teamId !== bracket.pick_f2)
      : null

    return [
      leftLoser ? buildTeamOption(leftLoser, rankings) : null,
      rightLoser ? buildTeamOption(rightLoser, rankings) : null,
    ]
  }

  const buildRoundOneConfigs = (matchNumbers) => matchNumbers.map(matchNumber => {
    const matchup = roundOneByMatchNumber[matchNumber]
    const firstField = `pick_${matchup.pickStart}`
    const secondField = `pick_${matchup.pickStart + 1}`

    return {
      field: `pick_r2_${Math.ceil(matchup.pickStart / 2)}`,
      label: `Match ${matchNumber}`,
      options: [
        bracket[firstField] ? buildTeamOption(bracket[firstField], rankings, roundOneSourceLabels[firstField]) : null,
        bracket[secondField] ? buildTeamOption(bracket[secondField], rankings, roundOneSourceLabels[secondField]) : null,
      ]
    }
  })

  const buildConfiguredMatches = (matchNumbers, matchFieldMap) => matchNumbers.map(matchNumber => ({
    field: matchFieldMap[matchNumber].field,
    label: `Match ${matchNumber}`,
    options: matchFieldMap[matchNumber].options.map(field => ({ field }))
  }))

  const leftRoundOneConfigs = buildRoundOneConfigs(ROUND_ONE_LAYOUT.left)
  const rightRoundOneConfigs = buildRoundOneConfigs(ROUND_ONE_LAYOUT.right)
  const leftRoundTwoConfigs = buildConfiguredMatches(ROUND_TWO_LAYOUT.left, ROUND_TWO_MATCH_FIELDS)
  const rightRoundTwoConfigs = buildConfiguredMatches(ROUND_TWO_LAYOUT.right, ROUND_TWO_MATCH_FIELDS)
  const leftQuarterConfigs = buildConfiguredMatches(QUARTER_LAYOUT.left, QUARTER_MATCH_FIELDS)
  const rightQuarterConfigs = buildConfiguredMatches(QUARTER_LAYOUT.right, QUARTER_MATCH_FIELDS)
  const leftSemiConfigs = buildConfiguredMatches(SEMI_LAYOUT.left, SEMI_MATCH_FIELDS)
  const rightSemiConfigs = buildConfiguredMatches(SEMI_LAYOUT.right, SEMI_MATCH_FIELDS)
  const mobileRoundOneConfigs = [...leftRoundOneConfigs, ...rightRoundOneConfigs]
  const mobileRoundTwoConfigs = [...leftRoundTwoConfigs, ...rightRoundTwoConfigs]
  const mobileQuarterConfigs = [...leftQuarterConfigs, ...rightQuarterConfigs]
  const mobileSemiConfigs = [...leftSemiConfigs, ...rightSemiConfigs]

  const desktopColumn = (children, className = '') => (
    <div className={`bracket-v2-column ${className}`}>
      {children}
    </div>
  )

  const mobileColumn = (title, date, children, className = '') => (
    <section className={`bracket-v2-mobile-column ${className}`} key={`${title}-${date}`}>
      <div className="round-details primary wc-font wc-secondary">{title}<span className="date wc-quaternary">{date}</span></div>
      <div className="bracket-v2-mobile-column-body">{children}</div>
    </section>
  )

  useEffect(() => {
    const scroller = mobileScrollerRef.current
    if (!scroller) return undefined

    let frameId = null
    const updateFocusIndex = () => {
      frameId = null
      const firstColumn = scroller.querySelector('.bracket-v2-mobile-column')
      if (!firstColumn) return

      const track = scroller.querySelector('.bracket-v2-mobile-track')
      const gap = track ? parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0') : 0
      const columnWidth = firstColumn.getBoundingClientRect().width + gap
      if (!columnWidth) return

      const nextIndex = Math.min(4, Math.max(0, Math.round(scroller.scrollLeft / columnWidth)))
      setMobileFocusIndex(current => (current === nextIndex ? current : nextIndex))
    }

    const handleScroll = () => {
      if (frameId !== null) return
      frameId = window.requestAnimationFrame(updateFocusIndex)
    }

    updateFocusIndex()
    scroller.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', updateFocusIndex)

    return () => {
      scroller.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', updateFocusIndex)
      if (frameId !== null) window.cancelAnimationFrame(frameId)
    }
  }, [])

  return (
    <div className="bracket-v2-form">
      <header className="bracket-header">
        {showBackButton && <BackButton onClick={() => setStep(2)} text="Advancing Third Place Teams"/>}
        <div className="header-text bracket-v2-title">
          <p className="bracket-name wc-quaternary">Knockout Stage</p>
          <div className="bracket-v2-actions">
            {isAdmin && <Button onClick={autoFillKnockout}>Auto-fill bracket</Button>}
            {isAdmin && <Button onClick={clearKnockout}>Clear bracket</Button>}
            <Button loading={savePending} type="primary" onClick={saveBracket}>{saveLabel || 'Save bracket'}</Button>
          </div>
        </div>
      </header>

      <div className={`bracket-v2-mobile mobile-focus-${mobileFocusIndex}`} ref={mobileScrollerRef}>
        <div className="bracket-v2-mobile-track">
          {mobileColumn('Thirty-Two', formatRoundDate(scorecard.dates.round_1), renderConfiguredRound({
            bracket,
            configs: mobileRoundOneConfigs,
            handleChange,
            rankings,
          }), 'round-one-column')}
          {mobileColumn('Sixteen', formatRoundDate(scorecard.dates.round_2), renderConfiguredRound({
            bracket,
            configs: mobileRoundTwoConfigs,
            handleChange,
            rankings,
          }), 'round-two-column')}
          {mobileColumn('Quarters', formatRoundDate(scorecard.dates.quarter), renderConfiguredRound({
            bracket,
            configs: mobileQuarterConfigs,
            handleChange,
            rankings,
          }), 'quarter-column')}
          {mobileColumn('Semis', formatRoundDate(scorecard.dates.semi), renderConfiguredRound({
            bracket,
            configs: mobileSemiConfigs,
            handleChange,
            rankings,
          }), 'semi-column')}
          {mobileColumn('Final', formatRoundDate(scorecard.dates.final), [
            <div className="winner-flag-container bracket-v2-winner-flag" key="mobile-winner-flag">
              <img className="winner-flag" src={winnerFlag} alt={bracket.pick_winner ? `Winner flag for ${bracket.pick_winner}` : 'Winner flag placeholder'} />
            </div>,
            <KnockoutMatchupV2
              key="final-match"
              bracket={bracket}
              field="pick_winner"
              label="Match 104"
              onChange={handleChange}
              championship
              options={[
                bracket.pick_f1 ? buildTeamOption(bracket.pick_f1, rankings) : null,
                bracket.pick_f2 ? buildTeamOption(bracket.pick_f2, rankings) : null,
              ]}
            />,
            <div className="bracket-v2-third-heading round-details primary wc-secondary" key="mobile-third-heading">
              Third
              <span className="date wc-quaternary">{formatRoundDate(scorecard.dates.third)}</span>
            </div>,
            <KnockoutMatchupV2
              key="third-match"
              bracket={bracket}
              field="pick_third"
              label="Match 103"
              onChange={handleChange}
              championship
              options={thirdPlaceOptions()}
            />
          ], 'bracket-v2-mobile-center')}
        </div>
      </div>

      <div className="bracket-v2-desktop">
        <div className="bracket-v2-header-row">
          <div className="round-details primary wc-font wc-secondary">Thirty-Two<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.round_1)}</span></div>
          <div className="round-details primary wc-font wc-secondary">Sixteen<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.round_2)}</span></div>
          <div className="round-details primary wc-font wc-secondary">Quarters<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.quarter)}</span></div>
          <div className="round-details primary wc-font wc-secondary">Semis<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.semi)}</span></div>
          <div className="round-details primary wc-font wc-secondary">Final<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.final)}</span></div>
          <div className="round-details primary wc-font wc-secondary">Semis<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.semi)}</span></div>
          <div className="round-details primary wc-font wc-secondary">Quarters<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.quarter)}</span></div>
          <div className="round-details primary wc-font wc-secondary">Sixteen<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.round_2)}</span></div>
          <div className="round-details primary wc-font wc-secondary">Thirty-Two<span className="date wc-quaternary">{formatRoundDate(scorecard.dates.round_1)}</span></div>
        </div>
        <div className="bracket-v2-grid">
          {desktopColumn(renderConfiguredRound({ bracket, configs: leftRoundOneConfigs, handleChange, rankings }), 'side-left round-one-column')}
          {desktopColumn(renderConfiguredRound({ bracket, configs: leftRoundTwoConfigs, handleChange, rankings }), 'side-left round-two-column')}
          {desktopColumn(renderConfiguredRound({ bracket, configs: leftQuarterConfigs, handleChange, rankings }), 'side-left quarter-column')}
          {desktopColumn(renderConfiguredRound({ bracket, configs: leftSemiConfigs, handleChange, rankings }), 'side-left semi-column')}

          <div className="bracket-v2-column bracket-v2-center">
            <div className="winner-flag-container bracket-v2-winner-flag">
              <img className="winner-flag" src={winnerFlag} alt={bracket.pick_winner ? `Winner flag for ${bracket.pick_winner}` : 'Winner flag placeholder'} />
            </div>
            <KnockoutMatchupV2
              bracket={bracket}
              field="pick_winner"
              label="Match 104"
              onChange={handleChange}
              championship
              options={[
                bracket.pick_f1 ? buildTeamOption(bracket.pick_f1, rankings) : null,
                bracket.pick_f2 ? buildTeamOption(bracket.pick_f2, rankings) : null,
              ]}
            />
            <div className="bracket-v2-third-heading round-details primary wc-secondary">
              Third
              <span className="date wc-quaternary">{formatRoundDate(scorecard.dates.third)}</span>
            </div>
            <KnockoutMatchupV2
              bracket={bracket}
              field="pick_third"
              label="Match 103"
              onChange={handleChange}
              championship
              options={thirdPlaceOptions()}
            />
          </div>

          {desktopColumn(renderConfiguredRound({ bracket, configs: rightSemiConfigs, handleChange, rankings }), 'side-right semi-column')}
          {desktopColumn(renderConfiguredRound({ bracket, configs: rightQuarterConfigs, handleChange, rankings }), 'side-right quarter-column')}
          {desktopColumn(renderConfiguredRound({ bracket, configs: rightRoundTwoConfigs, handleChange, rankings }), 'side-right round-two-column')}
          {desktopColumn(renderConfiguredRound({ bracket, configs: rightRoundOneConfigs, handleChange, rankings }), 'side-right round-one-column')}
        </div>
      </div>
    </div>
  )
}
